import { memo, useState } from 'react';
import clsx from 'clsx';
import type { Message as MessageT, Reaction } from '@/lib/api';
import { Reactions } from '@/lib/api';
import { useChatStore } from '@/stores/chat';
import Markdown from './Markdown';
import UserAvatar from './UserAvatar';
import LinkPreview from './LinkPreview';
import PollMessage from './PollMessage';
import ReactionPicker from './ReactionPicker';
import { extractUrls } from '@/lib/links';
import { formatMessageStamp } from '@/lib/format';

interface Props {
  message: MessageT;
  isOwn: boolean;
  isGrouped: boolean; // consecutive same-sender
  showSender: boolean;
  onReply?: (m: MessageT) => void;
  onEdit?: (m: MessageT) => void;
  onDelete?: (m: MessageT) => void;
  onReplyClick?: (m: MessageT) => void;
}

function MessageImpl({ message, isOwn, isGrouped, showSender, onReply, onEdit, onDelete, onReplyClick }: Props) {
  const [menuOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const deleted = Boolean(message.deletedAt);
  const sender = message.sender;
  const senderName = sender?.displayName || sender?.username || 'Unknown';

  // Permission UI matches the backend delete policy
  // (apps/api/src/routes/messages.ts: isSender || canManage). The previous
  // UI labeled the button "(owner only)" but showed it to every member,
  // which let regular group members delete other users' messages — and
  // was a label lie in either direction.
  const myMembership = useChatStore((s) =>
    s.currentConversationId ? s.membersByConversation.get(s.currentConversationId) : undefined,
  );
  const currentUserId = useChatStore((s) => s.currentUserId);
  const me = myMembership?.find((m) => m.userId === currentUserId);
  const canManage = me?.role === 'owner' || me?.role === 'manager';
  const myUsername = me?.user?.username;

  // F-007: per-message reaction list from the chat store. Lazy-loaded by
  // opening the picker and kept in sync by WS `reaction_update` events.
  const reactions = useChatStore((s) => s.reactionsByMessage.get(message.id)) ?? [];
  const setReactions = useChatStore((s) => s.setReactions);

  // Pull first URL from content for inline preview
  const urls = extractUrls(message.content);
  const firstUrl = urls[0];

  function handlePick(emoji: string) {
    // F-007: optimistic toggle. Compute the next state locally so the
    // chip animates instantly, then reconcile when the server confirms.
    // On failure, restore the prior state and toast the error.
    const prev = useChatStore.getState().reactionsByMessage.get(message.id) ?? [];
    const optimistic = computeOptimisticReactions(prev, emoji);
    setReactions(message.id, optimistic);
    setPickerOpen(false);
    Reactions.toggle(message.id, emoji)
      .then(({ reactions: next }) => {
        // Server response carries `myReacted` for the actor only. We
        // trust counts from the server but preserve the per-user
        // myReacted bit in case the actor's reaction state was reordered
        // by a concurrent write.
        const merged = mergeServerReactions(prev, next);
        setReactions(message.id, merged);
      })
      .catch((err) => {
        // Restore prior state on failure so the chip doesn't lie.
        setReactions(message.id, prev);
        const reason = err && typeof err === 'object' && 'message' in err
          ? String((err as { message?: unknown }).message ?? '')
          : 'unknown error';
        const toast = (window as unknown as { __chatToast?: (m: string) => void }).__chatToast;
        toast?.(`Couldn't react: ${reason || 'unknown error'}`);
      });
  }

  function handleToggleChip(emoji: string) {
    // Clicking an existing chip toggles the same emoji (Reactions.toggle is
    // already idempotent on the server: it removes if present, adds if not).
    handlePick(emoji);
  }

  return (
    <div
      className={clsx(
        'group flex w-full gap-2 px-3',
        isOwn ? 'justify-end' : 'justify-start',
        isGrouped ? 'mt-0.5' : 'mt-3',
      )}
    >
      {!isOwn && (
        <div className="w-8 shrink-0">
          {!isGrouped && <UserAvatar name={senderName} src={sender?.avatarUrl} size={32} />}
        </div>
      )}
      <div className={clsx('flex max-w-[78%] flex-col', isOwn ? 'items-end' : 'items-start')}>
        {showSender && !isOwn && (
          <span className="mb-0.5 px-1 text-xs font-medium text-ink-500 dark:text-ink-300">{senderName}</span>
        )}
        <div
          className={clsx(
            'relative rounded-2xl px-3 py-2 shadow-soft',
            isOwn
              ? 'bg-brand-500 text-white'
              : 'bg-white text-ink-900 dark:bg-ink-800 dark:text-ink-50',
            deleted && 'italic opacity-70',
          )}
        >
          {message.replyTo && (
            <button
              type="button"
              onClick={() => onReplyClick?.(message.replyTo!)}
              className={clsx(
                'mb-1 block w-full max-w-xs truncate rounded-md border-l-2 px-2 py-1 text-left text-xs',
                isOwn
                  ? 'border-white/70 bg-white/10 text-white'
                  : 'border-brand-400 bg-ink-100 text-ink-700 dark:border-brand-500 dark:bg-ink-900/60 dark:text-ink-200',
              )}
            >
              <span className="block font-medium">{message.replyTo.sender?.displayName ?? 'Reply'}</span>
              <span className="block truncate opacity-80">{message.replyTo.content}</span>
            </button>
          )}

          {deleted ? (
            <span className="text-sm">This message was deleted.</span>
          ) : message.type === 'poll' && message.poll ? (
            <PollMessage poll={message.poll} />
          ) : (
            <>
              <Markdown content={message.content} currentUsername={myUsername} />
              {firstUrl && (
                <LinkPreview
                  url={firstUrl}
                  // The link preview sits inside the message bubble.
                  // Pick contrasting colors so the card stays legible on
                  // both the sender's dark brand bubble and the
                  // recipient's white/translucent bubble.
                  className={
                    isOwn
                      ? 'bg-white/10 text-white'
                      : 'bg-ink-100 text-ink-800 dark:bg-ink-700 dark:text-ink-100'
                  }
                />
              )}
            </>
          )}

          <div
            className={clsx(
              'mt-1 flex items-center gap-1 text-[10px]',
              isOwn ? 'text-white/80' : 'text-ink-500 dark:text-ink-300',
            )}
          >
            <span>{formatMessageStamp(message.createdAt)}</span>
            {message.editedAt && !deleted && <span aria-label="edited">· edited</span>}
          </div>
        </div>

        {/* F-007: reaction chips. Click toggles the same emoji on/off. */}
        {reactions.length > 0 && !deleted && (
          <div
            className={clsx(
              'mt-1 flex flex-wrap items-center gap-1',
              isOwn ? 'justify-end' : 'justify-start',
            )}
            data-testid="reaction-chips"
          >
            {reactions.map((r) => (
              <button
                key={r.emoji}
                type="button"
                onClick={() => handleToggleChip(r.emoji)}
                aria-pressed={r.myReacted}
                aria-label={`${r.emoji} ${r.count} ${r.count === 1 ? 'reaction' : 'reactions'}`}
                className={clsx(
                  'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs transition',
                  r.myReacted
                    ? 'border-brand-300 bg-brand-100 text-brand-800 dark:border-brand-700 dark:bg-brand-900/40 dark:text-brand-100'
                    : 'border-ink-200 bg-white text-ink-700 hover:bg-ink-100 dark:border-ink-700 dark:bg-ink-800 dark:text-ink-100 dark:hover:bg-ink-700',
                )}
              >
                <span aria-hidden="true">{r.emoji}</span>
                <span>{r.count}</span>
              </button>
            ))}
          </div>
        )}

        <div
          className={clsx(
            'mt-0.5 flex items-center gap-1 text-xs opacity-0 transition group-hover:opacity-100 focus-within:opacity-100',
            isOwn ? 'justify-end' : 'justify-start',
          )}
        >
          <button type="button" className="btn-ghost px-1 py-0.5 text-xs" onClick={() => onReply?.(message)} aria-label="Reply">
            Reply
          </button>
          {!deleted && message.type !== 'poll' && (
            <button
              type="button"
              className="btn-ghost px-1 py-0.5 text-xs"
              onClick={() => setPickerOpen((v) => !v)}
              aria-label="React"
              aria-expanded={pickerOpen}
            >
              React
            </button>
          )}
          {isOwn && !deleted && message.type !== 'poll' && (
            <>
              <button type="button" className="btn-ghost px-1 py-0.5 text-xs" onClick={() => onEdit?.(message)} aria-label="Edit">
                Edit
              </button>
              <button type="button" className="btn-ghost px-1 py-0.5 text-xs" onClick={() => onDelete?.(message)} aria-label="Delete">
                Delete
              </button>
            </>
          )}
          {!isOwn && canManage && !deleted && (
            <button type="button" className="btn-ghost px-1 py-0.5 text-xs" onClick={() => onDelete?.(message)} aria-label="Delete (manager)">
              Delete
            </button>
          )}
          {menuOpen && <span className="hidden" aria-hidden="true" />}
        </div>

        {/* F-007: reaction picker, rendered in-flow below the action row. */}
        {pickerOpen && !deleted && (
          <div className={clsx('mt-1', isOwn ? 'self-end' : 'self-start')}>
            <ReactionPicker
              messageId={message.id}
              reactions={reactions as Reaction[]}
              onPick={handlePick}
              onClose={() => setPickerOpen(false)}
            />
          </div>
        )}
      </div>
      {isOwn && (
        <div className="w-8 shrink-0">
          {!isGrouped && <UserAvatar name={senderName} src={sender?.avatarUrl} size={32} />}
        </div>
      )}
    </div>
  );
}

const Message = memo(MessageImpl, (prev, next) => {
  return (
    prev.message === next.message &&
    prev.isOwn === next.isOwn &&
    prev.isGrouped === next.isGrouped &&
    prev.showSender === next.showSender &&
    prev.onReply === next.onReply &&
    prev.onEdit === next.onEdit &&
    prev.onDelete === next.onDelete &&
    prev.onReplyClick === next.onReplyClick
  );
});

/**
 * Compute the next `reactions` array after the actor toggles `emoji`:
 * - If the actor already reacted with this emoji, decrement count and
 *   clear their `myReacted` flag.
 * - Otherwise, increment count and set their `myReacted` flag.
 * Pure: takes an immutable input and returns a new array.
 */
function computeOptimisticReactions(prev: Reaction[], emoji: string): Reaction[] {
  const idx = prev.findIndex((r) => r.emoji === emoji);
  if (idx === -1) {
    return [...prev, { emoji, count: 1, myReacted: true }];
  }
  const r = prev[idx];
  if (r.myReacted) {
    if (r.count <= 1) {
      // Last reaction for this emoji — drop the entry.
      return prev.filter((_, i) => i !== idx);
    }
    return prev.map((p, i) => (i === idx ? { ...p, count: p.count - 1, myReacted: false } : p));
  }
  return prev.map((p, i) => (i === idx ? { ...p, count: p.count + 1, myReacted: true } : p));
}

/**
 * Merge a server response into the local store. Counts from the server
 * are authoritative; the only thing we keep from the local state is the
 * actor's own `myReacted` flag for emojis not in the server response
 * (defensive — the server response should always include all emojis).
 */
function mergeServerReactions(local: Reaction[], server: Reaction[]): Reaction[] {
  const localByEmoji = new Map(local.map((r) => [r.emoji, r.myReacted]));
  return server.map((r) => ({
    emoji: r.emoji,
    count: r.count,
    myReacted: r.myReacted ?? localByEmoji.get(r.emoji) ?? false,
  }));
}

export default Message;
